import { formatISO, isPast } from 'date-fns'
import { useContext, useEffect, useState } from 'react'
import api from '../../../api'
import { CampaignContext, ProjectContext, TemplateContext } from '../../../contexts'
import { Campaign, CampaignLaunchParams, Preferences, CampaignQuietHours, CampaignUpdateParams, LaunchApproval, ProjectAdmin, projectRoles } from '../../../types'
import { Column, Columns } from '../../../ui/Columns'
import RadioInput from '../../../ui/form/RadioInput'
import SwitchField from '../../../ui/form/SwitchField'
import FormWrapper from '../../../ui/form/FormWrapper'
import Modal from '../../../ui/Modal'
import Alert from '../../../ui/Alert'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { toast } from 'react-hot-toast/headless'
import { useNavigate } from 'react-router'
import DateTimeField from './DateTimeField'
import { Button, InfoTable } from '../../../ui'
import { formatDate, snakeToTitle } from '../../../utils'
import { PreferencesContext } from '../../../ui/PreferencesContext'
import { localeOption } from '../template/TemplateContextProvider'
import { DelimitedLists } from '../ui/DelimitedItems'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import TextInput from '../../../ui/form/TextInput'
import { quietHoursLabel } from '../ui/QuietHoursLabel'
import { derivedEndsAt, isInAppBroadcast } from '../inAppBroadcast'
import { hasDismissAction } from '../template/in-app/dismissAction'
import './LaunchCampaign.css'

type LaunchMode = 'launch' | 'approve' | 'review'
type LaunchStep = 'schedule' | 'approval' | 'confirm'

interface LaunchCampaignProps {
    open: boolean
    onClose: (open: boolean) => void
    pendingApproval?: LaunchApproval
    mode?: 'approve' | 'review'
    onApprovalComplete?: () => void
}

interface LaunchConfirmationProps {
    campaign: Campaign
    params: CampaignLaunchParams
    mode: LaunchMode
    onLaunch: () => void
    onReject: () => void
    onCancel: () => void
    onRequestApproval: () => void
    onBack: () => void
}

function LaunchConfirmation({ campaign, params, mode, onLaunch, onReject, onCancel, onRequestApproval, onBack }: LaunchConfirmationProps) {
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)
    const [project] = useContext(ProjectContext)
    const { variants, variantMap, locales } = useContext(TemplateContext)
    const [estimatedSize, setEstimatedSize] = useState<number | undefined>()
    const variantList = Object.entries(variantMap).map(([locale, variants]) => `${localeOption(locale).shortLabel} (${variants.map(v => v.name).join(', ')})`).join(', ')

    useEffect(() => {
        api.campaigns.size(campaign.project_id, campaign.id)
            .then((resp) => setEstimatedSize(resp.data.estimated_send_size))
            .catch(() => setEstimatedSize(undefined))
    }, [campaign])

    const renderButtons = () => {
        if (mode === 'review') {
            return (
                <Button variant="destructive" onClick={onCancel}>
                    {t('cancel_request')}
                </Button>
            )
        }
        if (mode === 'approve') {
            return (
                <>
                    <Button variant="destructive" onClick={onReject}>
                        {t('reject')}
                    </Button>
                    <Button variant="primary" onClick={onLaunch}>
                        {t('approve_and_launch')}
                    </Button>
                </>
            )
        }
        return (
            <>
                <Button variant="secondary" onClick={onBack}>
                    {t('back')}
                </Button>
                <Button variant="primary" onClick={onRequestApproval}>
                    {t('approval_request')}
                </Button>
            </>
        )
    }

    return (
        <>
            <p>{t('launch_confirmation_subtitle')}</p>
            <InfoTable rows={{
                [t('name')]: campaign.name,
                [t('channel')]: t(campaign.channel),
                [t('send_at')]: params.send_at ? params.send_at : t(snakeToTitle('now')),
                [t('send_in_user_timezone')]: params.send_in_user_timezone,
                ...params.ends_at && { [t('ends_at')]: endsAtLabel(campaign, params, preferences, project.timezone, t) },
                ...campaign.settings?.quiet_hours && {
                    [t('quiet_hours')]: quietHoursLabel(campaign.settings.quiet_hours, t),
                },
                [t('estimated_recipients')]: estimatedSize !== undefined ? `~${estimatedSize.toLocaleString()}` : t('loading_ellipsis'),
                [t('send_lists')]: DelimitedLists({ lists: campaign.lists }),
                [t('exclusion_lists')]: DelimitedLists({ lists: campaign.exclusion_lists }),
            }} />
            {estimatedSize === 0 && (
                <Alert variant="warn" size="small" title={t('launch_zero_recipients_title')}>
                    {t('launch_zero_recipients_desc')}
                </Alert>
            )}
            {variants.length > 1 && (
                <Alert variant="info" size="small" title={t('launch_multiple_variants_title')}>
                    {t('launch_multiple_variants_desc')}
                </Alert>
            )}
            <InfoTable rows={{
                ...variants.length > 1 ? { [t('variants')]: variantList } : {},
                [t('translations')]: locales.map(l => l.label).join(', '),
            }} />
            <div className="launch-confirmation-buttons">
                {renderButtons()}
            </div>
        </>
    )
}

interface LaunchConfirmationPublisherProps {
    campaign: Campaign
    params: CampaignLaunchParams
    onLaunch: () => void
    onBack: () => void
}

function LaunchConfirmationPublisher({ campaign, params, onLaunch, onBack }: LaunchConfirmationPublisherProps) {
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)
    const [project] = useContext(ProjectContext)
    const { variants, variantMap, locales } = useContext(TemplateContext)
    const [estimatedSize, setEstimatedSize] = useState<number | undefined>()
    const variantList = Object.entries(variantMap).map(([locale, variants]) => `${localeOption(locale).shortLabel} (${variants.map(v => v.name).join(', ')})`).join(', ')

    useEffect(() => {
        api.campaigns.size(campaign.project_id, campaign.id)
            .then((resp) => setEstimatedSize(resp.data.estimated_send_size))
            .catch(() => setEstimatedSize(undefined))
    }, [campaign])

    return (
        <>
            <p>{t('launch_confirmation_subtitle')}</p>
            <InfoTable rows={{
                [t('name')]: campaign.name,
                [t('channel')]: t(campaign.channel),
                [t('send_at')]: params.send_at ? params.send_at : t(snakeToTitle('now')),
                [t('send_in_user_timezone')]: params.send_in_user_timezone,
                ...params.ends_at && { [t('ends_at')]: endsAtLabel(campaign, params, preferences, project.timezone, t) },
                ...campaign.settings?.quiet_hours && {
                    [t('quiet_hours')]: quietHoursLabel(campaign.settings.quiet_hours, t),
                },
                [t('estimated_recipients')]: estimatedSize !== undefined ? `~${estimatedSize.toLocaleString()}` : t('loading_ellipsis'),
                [t('send_lists')]: DelimitedLists({ lists: campaign.lists }),
                [t('exclusion_lists')]: DelimitedLists({ lists: campaign.exclusion_lists }),
            }} />
            {estimatedSize === 0 && (
                <Alert variant="warn" size="small" title={t('launch_zero_recipients_title')}>
                    {t('launch_zero_recipients_desc')}
                </Alert>
            )}
            {variants.length > 1 && (
                <Alert variant="info" size="small" title={t('launch_multiple_variants_title')}>
                    {t('launch_multiple_variants_desc')}
                </Alert>
            )}
            <InfoTable rows={{
                ...variants.length > 1 ? { [t('variants')]: variantList } : {},
                [t('translations')]: locales.map(l => l.label).join(', '),
            }} />
            <div className="launch-confirmation-buttons">
                <Button variant="secondary" onClick={onBack}>
                    {t('back')}
                </Button>
                <Button variant="primary" onClick={onLaunch}>
                    {t(campaign.send_at ? 'reschedule' : 'launch')}
                </Button>
            </div>
        </>
    )
}

const endsAtLabel = (campaign: Campaign, params: CampaignLaunchParams, preferences: Preferences, timezone: string, t: TFunction) => {
    const sendAt = params.send_at ? new Date(params.send_at) : new Date()
    const source = derivedEndsAt(campaign, sendAt).source === 'message' ? t('ends_at_from_message') : t('ends_at_default')
    return `${formatDate(preferences, params.ends_at!, 'EEE, MMM d, p', timezone)} (${timezone}), ${source}`
}

interface LaunchFormParams extends CampaignLaunchParams {
    quiet_hours_enabled?: boolean
    quiet_hours?: CampaignQuietHours
}

interface LaunchFormProps {
    campaign: Campaign
    onSubmit: (data: LaunchFormParams) => Promise<void>
}

function LaunchForm({ campaign, onSubmit }: LaunchFormProps) {
    const { t } = useTranslation()
    const quietHoursActions = [
        { value: 'skip', label: t('quiet_hours_action_skip') },
        { value: 'delay', label: t('quiet_hours_action_delay') },
    ]
    const renderForm = ({ form }: { form: UseFormReturn<LaunchFormParams> }) => {
        const launchType = useWatch({
            control: form.control,
            name: 'launch_type',
        })
        const quietHoursEnabled = useWatch({
            control: form.control,
            name: 'quiet_hours_enabled',
        })
        return (
            <>
                <RadioInput.Field
                    form={form}
                    name="launch_type"
                    label={t('launch_period')}
                    options={[{ key: 'now', label: t('now') }, { key: 'later', label: t('schedule') }]}
                />
                {launchType === 'later' && (
                    <>
                        <DateTimeField
                            control={form.control}
                            name="send_at"
                            label={t('send_at')}
                            required
                        />
                        <SwitchField
                            form={form}
                            name="send_in_user_timezone"
                            label={t('send_in_user_timezone')}
                            subtitle={t('send_in_user_timezone_desc')}
                        />
                        <SwitchField
                            form={form}
                            name="quiet_hours_enabled"
                            label={t('quiet_hours_enable')}
                            subtitle={t('quiet_hours_desc')}
                        />
                        {quietHoursEnabled && (
                            <Columns>
                                <Column>
                                    <TextInput.Field
                                        form={form}
                                        name="quiet_hours.start"
                                        type="time"
                                        label={t('quiet_hours_start')}
                                        required
                                    />
                                </Column>
                                <Column>
                                    <TextInput.Field
                                        form={form}
                                        name="quiet_hours.end"
                                        type="time"
                                        label={t('quiet_hours_end')}
                                        required
                                    />
                                </Column>
                                <Column>
                                    <SingleSelect.Field
                                        form={form}
                                        name="quiet_hours.action"
                                        label={t('quiet_hours_action')}
                                        options={quietHoursActions}
                                        toValue={v => v.value}
                                        required
                                    />
                                </Column>
                            </Columns>
                        )}
                    </>
                )}
            </>
        )
    }

    return (
        <>
            <p>{t('launch_subtitle')}</p>
            <FormWrapper<LaunchFormParams>
                submitLabel={t('continue')}
                defaultValues={{
                    quiet_hours_enabled: !!campaign.settings?.quiet_hours,
                    quiet_hours: campaign.settings?.quiet_hours ?? { start: '22:00', end: '08:00', action: 'delay' },
                }}
                onSubmit={onSubmit}
            >
                {form => renderForm({ form })}
            </FormWrapper>
        </>
    )
}

interface ApprovalRequestFormParams extends CampaignLaunchParams {
    approver_id?: number
    request_note?: string
}

interface ApprovalRequestFormProps {
    params: CampaignLaunchParams
    onSubmit: (data: ApprovalRequestFormParams) => Promise<void>
    onBack: () => void
}

function ApprovalRequestForm({ params, onSubmit, onBack }: ApprovalRequestFormProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [approvers, setApprovers] = useState<ProjectAdmin[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        api.approvals.approvers(project.id)
            .then(setApprovers)
            .finally(() => setLoading(false))
    }, [project.id])

    if (loading) {
        return <p>{t('loading_ellipsis')}</p>
    }

    return (
        <>
            <p>{t('approval_request_subtitle')}</p>
            <FormWrapper<ApprovalRequestFormParams>
                defaultValues={{ ...params }}
                showSubmitButton={false}
                onSubmit={onSubmit}
            >
                {(form, { isLoading, isValid, disabled }) => (
                    <>
                        <SingleSelect.Field
                            form={form}
                            name="approver_id"
                            label={t('approver')}
                            options={approvers}
                            toValue={(admin) => admin.id}
                            getOptionDisplay={(admin) => admin.first_name && admin.last_name
                                ? `${admin.first_name} ${admin.last_name} (${admin.email})`
                                : admin.email}
                            required
                        />
                        <TextInput.Field
                            form={form}
                            name="request_note"
                            label={t('approval_request_note')}
                            subtitle={t('approval_request_note_subtitle')}
                            textarea
                        />
                        <div className="approval-form-buttons">
                            <Button variant="secondary" onClick={onBack}>
                                {t('back')}
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isLoading}
                                disabled={!isValid || disabled}
                            >
                                {t('approval_request')}
                            </Button>
                        </div>
                    </>
                )}
            </FormWrapper>
        </>
    )
}

function canPublish(role?: string): boolean {
    if (!role) return false
    return projectRoles.indexOf(role as typeof projectRoles[number]) >= projectRoles.indexOf('publisher')
}

export default function LaunchCampaign({ open, onClose, pendingApproval, mode, onApprovalComplete }: LaunchCampaignProps) {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [campaign, setCampaign] = useContext(CampaignContext)
    const [error, setError] = useState<string | undefined>()
    const [launchParams, setLaunchParams] = useState<CampaignLaunchParams | undefined>()
    const [step, setStep] = useState<LaunchStep>(pendingApproval ? 'confirm' : 'schedule')

    const isPublisher = canPublish(project.role)
    const launchMode: LaunchMode = mode ?? 'launch'

    useEffect(() => {
        if (pendingApproval?.launch_params) {
            setLaunchParams(pendingApproval.launch_params as CampaignLaunchParams)
            setStep('confirm')
        }
    }, [pendingApproval])

    async function handleLaunch() {
        if (!launchParams) return
        const { send_at, send_in_user_timezone, launch_type, ends_at } = launchParams
        const sendAt = send_at ? Date.parse(send_at) : new Date()

        if (launch_type === 'later'
            && isPast(sendAt)
            && !confirm(t('launch_past_confirm'))) {
            return
        }

        if (campaign.channel === 'in_app'
            && campaign.templates.some(template => !hasDismissAction(template.data.html))
            && !confirm(t('inapp_no_dismiss_confirm'))) {
            return
        }

        const params: CampaignLaunchParams = {
            send_at: formatISO(sendAt),
            state: 'scheduled',
            send_in_user_timezone,
            ...ends_at && { ends_at },
        }

        try {
            if (pendingApproval && mode === 'approve') {
                await api.approvals.approve(project.id, pendingApproval.id, {})
                onApprovalComplete?.()
            } else {
                const value = await api.campaigns.launch(project.id, campaign.id, params)
                setCampaign(value)
            }
            onClose(false)
            await navigate('delivery')
        } catch (error: any) {
            if (error?.response?.data) {
                setError(error?.response?.data?.error)
            }
        }
    }

    async function handleRequestApproval(data: ApprovalRequestFormParams) {
        if (!launchParams) return
        const { send_at, send_in_user_timezone, launch_type, ends_at } = launchParams
        const sendAt = send_at ? Date.parse(send_at) : new Date()

        try {
            await api.campaigns.requestApproval(project.id, campaign.id, {
                state: 'scheduled',
                send_at: formatISO(sendAt),
                send_in_user_timezone: launch_type === 'later' ? send_in_user_timezone : false,
                ...ends_at && { ends_at },
                request_note: data.request_note,
                approver_id: data.approver_id,
            })
            toast.success(t('approval_requested'))
            onApprovalComplete?.()
            onClose(false)
        } catch (error: any) {
            if (error?.response?.data) {
                setError(error?.response?.data?.error)
            }
        }
    }

    async function handleReject() {
        if (!pendingApproval) return
        try {
            await api.approvals.reject(project.id, pendingApproval.id, {})
            onApprovalComplete?.()
            onClose(false)
        } catch (error: any) {
            if (error?.response?.data) {
                setError(error?.response?.data?.error)
            }
        }
    }

    async function handleCancel() {
        if (!pendingApproval) return
        try {
            await api.approvals.cancel(project.id, pendingApproval.id)
            toast.success(t('approval_cancelled'))
            onApprovalComplete?.()
            onClose(false)
        } catch (error: any) {
            if (error?.response?.data) {
                setError(error?.response?.data?.error)
            }
        }
    }

    function handleCloseModal() {
        onClose(false)
        setLaunchParams(undefined)
        setStep('schedule')
        setError(undefined)
    }

    const modalTitle = step === 'approval'
        ? t('approval_request')
        : mode
            ? t('approval_review')
            : t('launch_campaign')

    const renderConfirmation = () => {
        if (!launchParams) return null

        // Publisher launching directly (no pending approval)
        if (isPublisher && !pendingApproval) {
            return (
                <LaunchConfirmationPublisher
                    campaign={campaign}
                    params={launchParams}
                    onLaunch={handleLaunch}
                    onBack={() => setStep('schedule')}
                />
            )
        }

        // Reviewing/approving a pending approval or non-publisher requesting
        return (
            <LaunchConfirmation
                campaign={campaign}
                params={launchParams}
                mode={launchMode}
                onLaunch={handleLaunch}
                onReject={handleReject}
                onCancel={handleCancel}
                onRequestApproval={() => setStep('approval')}
                onBack={() => setStep('schedule')}
            />
        )
    }

    return (
        <Modal
            title={modalTitle}
            open={open}
            onClose={handleCloseModal}
            size="regular"
        >
            {error && <Alert variant="error" title="Error">{error}</Alert>}
            {step === 'schedule' && (
                <LaunchForm campaign={campaign} onSubmit={async ({ quiet_hours_enabled, quiet_hours, ...params }) => {
                    // Quiet hours is only shown (and only persisted) when the
                    // campaign is being scheduled for a future time.
                    if (params.launch_type === 'later') {
                        try {
                            const updateParams: CampaignUpdateParams = {
                                settings: {
                                    link_wrap: campaign.settings?.link_wrap ?? false,
                                    priority: campaign.settings?.priority ?? 'none',
                                    quiet_hours: quiet_hours_enabled ? quiet_hours : undefined,
                                    in_app_delivery: campaign.settings?.in_app_delivery,
                                },
                            }
                            const updated = await api.campaigns.update(project.id, campaign.id, updateParams)
                            setCampaign(updated)
                        } catch (error: any) {
                            if (error?.response?.data) setError(error?.response?.data?.error)
                            return
                        }
                    }
                    const sendAt = params.launch_type === 'later' && params.send_at ? new Date(params.send_at) : new Date()
                    setLaunchParams(isInAppBroadcast(campaign)
                        ? { ...params, ends_at: derivedEndsAt(campaign, sendAt).date.toISOString() }
                        : params)
                    setStep('confirm')
                }} />
            )}
            {step === 'confirm' && renderConfirmation()}
            {step === 'approval' && launchParams && (
                <ApprovalRequestForm
                    params={launchParams}
                    onSubmit={handleRequestApproval}
                    onBack={() => setStep('confirm')}
                />
            )}
        </Modal>
    )
}
